import React, { useEffect, useRef, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import { Link, useLocation } from 'react-router-dom';

import { animateH2Elements } from './HomeAnimations.js';
import { useSelectedServices } from '../../components/Sections/ServicesSection/SelectedServicesContext';
import { PreloadProjectCards } from '../../services/NotionProjectCards';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useBottomSheet } from '../../components/Modals/BottomSheet/BottomSheetContext';

import Section from '../../components/Sections/Section';
import Services from '../../components/Sections/ServicesSection/ServicesSection.js';
import HeroSection from '../../components/Sections/HeroSection/HeroSection';
import Button from '../../components/Button/Button';
import FormSection from '../../components/Sections/FormSection/FormSection.js';

import teamphoto from '../../assets/team-profile-2024.jpeg';

import './Home.css';

gsap.registerPlugin(ScrollTrigger);

const Home = () => {
  const servicesRef = useRef(null);
  const h2Ref = useRef(null);
  const { selectedServices } = useSelectedServices();
  const formSectionRef = useRef(null);
  const servicesSectionRef = useRef(null);
  const heroRef = useRef(null);
  const intakeButtonRef = useRef(null);
  const showIntakeButton = useState(false);
  const fadeRef = useRef(null);
  const { openBottomSheet } = useBottomSheet();
  const [selectedService, setSelectedService] = useState(null);
  const location = useLocation();

  const animateH2 = useCallback((entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        animateH2Elements([entry.target]);
        observer.unobserve(entry.target);
      }
    });
  }, []);

  const scrollToServicesSection = () => {
    if (servicesSectionRef.current) {
      const yOffset = -60;
      const y = servicesSectionRef.current.offsetTop + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1,
    };

    const observer = new IntersectionObserver(animateH2, options);

    let h2Elements = [];
    if (h2Ref.current) {
      h2Elements = h2Ref.current.querySelectorAll('h1, h2');
      h2Elements.forEach((el) => observer.observe(el));
    }

    return () => {
      if (h2Elements.length > 0) {
        setTimeout(() => {
          setTimeout(() => {
            h2Elements.forEach((el) => observer.unobserve(el));
          }, 1000);
        }, 500);
      }
      observer.disconnect();
    };
  }, [animateH2]);

  useEffect(() => {
    PreloadProjectCards('069a0aee6d3d4f8aa2755b93ba042d2a');
  }, []);

  const scrollToForm = useCallback(() => {
    if (formSectionRef.current) {
      const yOffset = -60;
      const y = formSectionRef.current.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    const handleScrollToForm = () => {
      setTimeout(scrollToForm, 100);
    };

    window.addEventListener('scrollToForm', handleScrollToForm);

    return () => {
      window.removeEventListener('scrollToForm', handleScrollToForm);
    };
  }, [scrollToForm]);

  useEffect(() => {
    const element = fadeRef.current;

    gsap.set(element, { opacity: 0 });

    ScrollTrigger.create({
      trigger: element,
      start: 'top bottom-=100',
      onEnter: () => {
        gsap.to(element, {
          opacity: 1,
          duration: 2,
          ease: 'power2.inOut'
        });
      },
      once: true
    });
  }, []);

  const titleTexts = [''];
  const paragraphTexts = [''];

  const cardsData = [
    {
      imageSrc: 'https://i.pinimg.com/originals/e5/7e/e3/e57ee38c07ab1cee69ac47a3dc624c69.gif',
      text: 'Design',
      description: "Wij ontwerpen marketing-, eCommerce- of backoffice websites of diverse (IoT) producten of oplossingen voor jouw bedrijf. ",
      pill: "Ontwerp",
    },
    {
      imageSrc: 'https://i.pinimg.com/originals/40/a5/a6/40a5a6db0ded2770c819c60ad516c3e9.gif',
      text: 'Experience',
      description: "Wij helpen je met het ontwikkelen van impactvolle ervaringen die blijven kleven bij je klanten. Afhankelijk van het medium zullen wij ons netwerk van proffesionals benaderen voor een kwalitatief resultaat.",
      pill: "Informatie",
    },
    {
      imageSrc: 'https://i.pinimg.com/originals/cb/29/f6/cb29f60faf9eaffbf867462b485cd118.gif',
      text: 'eCommerce',
      description: "Wij maken volledig geautomatiseerde eCommerce oplossingen die jouw merk versterken en je klanten een soepele, plezierige digitale shopervaring bieden. Wij hanteren lage transactie marges dankzij onze partner: Revolut.",
      pill: "eCommerce",
    },
    {
      imageSrc: 'https://i.pinimg.com/originals/5a/4c/a7/5a4ca79071e547d4c39ea3884cecac41.gif',
      text: 'Data',
      description: "Wij helpen jouw bedrijf om je data te structureren en nuttig te maken. Of je nu te maken hebt met versnipperde gegevens of complexe datasets, wij brengen orde in de chaos en zetten data om in waardevolle inzichten.",
      pill: "Data",
    },
    {
      imageSrc: 'https://i.pinimg.com/originals/87/90/33/8790331e70a91bc682903e659617d9b8.gif',
      text: 'Automation',
      description: "Wij begrijpen als geen ander dat je jouw bedrijf efficiënter wilt laten werken, maar waar begin je? En waar ligt de grens voor efficientie nou echt? Wij helpen je bedrijf graag om deze vraagstukken te beantwoorden (en te automatiseren), zodat je kan focussen op wat echt belangrijk is. Met automatisering is 'the sky the limit'.",
      pill: "Technologie",
    },
    {
      imageSrc: 'https://i.pinimg.com/originals/2d/c4/4c/2dc44c226729e7dd4d54d32ad7113102.gif',
      text: 'AI development',
      description: "Tegenwoordig is AI bijna niet meer weg te denken voor moderne software. FlintandSteel ontwikkelt custom of simpele AI toepassingen van hoge kwalitatief.",
      pill: "AI",
    },
  ];

  const openFirstService = useCallback(() => {
    if (cardsData.length > 0) {
      openBottomSheet(cardsData[0]);
    }
  }, [cardsData, openBottomSheet]);

  return (
    <div className="homepage" ref={h2Ref}>
      <Section className="hero section--full-width" ref={heroRef}>
        <HeroSection
          scrollToServices={scrollToServicesSection}
          titleTexts={titleTexts}
          paragraphTexts={paragraphTexts}
        />
      </Section>

      <Section className="services" ref={servicesSectionRef} id="services">
        <div className="header-wrapper">
          <div className="secondary-wrapper">
            <h1 className="clamp">Wij ontwikkelen unieke digitale ervaringen die jouw bedrijf helpt vlammen.</h1>
            <p ref={fadeRef} className="label primary" >
              FlintandSteel - vuursteen en vuurslag - is een studio gebaseerd in Amsterdam.
              Wij zijn gespecialiseerd in het realiseren van digitale ideeën waar je versteld van zal staan.
            </p>
            <div className="hero-button-wrapper">
              <Button variant="outlined" onClick={openFirstService} ref={fadeRef}>Bekijk onze diensten</Button>
            </div>
          </div>
        </div>
        <Services
          ref={servicesRef}
          services={cardsData}
          selectedService={selectedService}
          setSelectedService={setSelectedService}
        />
        {showIntakeButton && selectedServices.length > 0 && (
          <Button
            ref={intakeButtonRef}
            className="intake-button"
            onClick={(e) => {
              e.preventDefault();
              scrollToForm();
            }}
          >
            Stuur een berichtje
          </Button>
        )}
        {location.pathname === '/' && (
          <div className="about-us">
            <div className="title-wrapper" ref={fadeRef}>
              {/* <div className="pill">
                <p className="pill-text">Over ons</p>
              </div> */}
              <h2>
                Wij helpen je met het vinden van de juiste tools voor de job.
              </h2>
              <Button variant="outlined" to="/about" ref={fadeRef}>Meer over ons</Button>
            </div>
            <Link to="/about" className="team-photo-link">
              <img
                src={teamphoto}
                alt="FlintandSteel team photo 2024"
                loading="lazy"
                className="team-photo"
                ref={fadeRef}
              />
            </Link>
          </div>
        )}
      </Section>
      <div id="form-section"></div>
      <FormSection
        className="form"
        ref={formSectionRef}
        selectedServices={selectedServices}
        scrollToServicesSection={scrollToServicesSection}
      />
    </div>
  );
};

export default Home;
